import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, FileText, Briefcase, MapPin, ArrowRight, ChevronRight, Search, Pencil } from 'lucide-react';
import jobService, { JobWithEmployer } from '../../services/jobService';
import serviceManagementService from '../../services/serviceManagementService';
import cvTemplateService from '../../services/cvTemplateService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import './JobManagement.scss';

// Promotion info for a job
interface JobPromotion {
  jobId: number;
  startsAt: string;
  expiresAt: string;
}

const JobManagement: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const [jobs, setJobs] = useState<JobWithEmployer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobPromotions, setJobPromotions] = useState<Map<number, JobPromotion>>(new Map());
  const [selectedJobs, setSelectedJobs] = useState<number[]>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [categoryImages, setCategoryImages] = useState<{ [value: string]: string | null }>({});
  const isRecruitmentAgency = !!user?.is_recruitment_agency;

  useEffect(() => {
    loadJobs();
    loadPromotedJobs();
    loadCategoryImages();
  }, []);

  const loadJobs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await jobService.getMyJobs();
      if (result.success && result.data) {
        setJobs(result.data.jobs as JobWithEmployer[]);
      } else {
        setError(result.message || 'Nepavyko užkrauti darbo skelbimų');
      }
    } catch (error) {
      setError('Įvyko klaida užkraunant darbo skelbimus');
    } finally {
      setLoading(false);
    }
  };

  const loadPromotedJobs = async () => {
    try {
      const purchases = await serviceManagementService.getActivePurchases();
      const promotionsMap = new Map<number, JobPromotion>();
      
      purchases
        .filter(p => p.service_type === 'job_promotion' && p.job_id && p.expires_at)
        .forEach(p => {
          promotionsMap.set(p.job_id!, {
            jobId: p.job_id!,
            startsAt: p.starts_at || p.created_at,
            expiresAt: p.expires_at!
          });
        });
      
      setJobPromotions(promotionsMap);
    } catch (error) {
      console.error('Error loading promoted jobs:', error);
    }
  };

  const handleCreateJob = () => {
    navigate('/jobs/create');
  };

  const handleViewApplications = (jobId: number) => {
    navigate(`/jobs/${jobId}/applications`);
  };

  const handleEditJob = (jobId: number) => {
    navigate(`/jobs/edit/${jobId}?mode=employer`);
  };

  const handlePublishJob = async (jobId: number) => {
    try {
      const result = await jobService.publishJob(jobId);
      if (result.success) {
        loadJobs(); // Reload to get updated status
      } else {
        showToast(result.message || 'Nepavyko publikuoti darbo skelbimo', 'error');
      }
    } catch (error) {
      showToast('Įvyko klaida publikuojant darbo skelbimą', 'error');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const loadCategoryImages = async () => {
    try {
      const [activityImages, workFieldImages] = await Promise.all([
        cvTemplateService.getCategoryImages('company_activity'),
        cvTemplateService.getCategoryImages('work_field')
      ]);
      setCategoryImages({ ...workFieldImages, ...activityImages });
    } catch (error) {
      console.error('Error loading category images:', error);
    }
  };

  const DEFAULT_BG = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop';

  const getCategoryBackgroundImage = (job: JobWithEmployer): string => {
    const activity = job.employer_company_activity || job.company_activity;
    const workField = job.work_field;

    const imageUrl = (activity && categoryImages[activity])
      || (workField && categoryImages[workField])
      || null;

    if (imageUrl) {
      if (imageUrl.startsWith('http')) return imageUrl;
      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
      return `${baseUrl}${imageUrl}`;
    }

    return DEFAULT_BG;
  };

  const getCompanyLogoUrl = (filename?: string): string | null => {
    if (!filename) return null;
    const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    return `${API_BASE_URL}/uploads/profiles/${filename}`;
  };

  const uniqueClientNames = React.useMemo(() => {
    const names = new Set<string>();
    jobs.forEach(j => { if ((j as any).client_name) names.add((j as any).client_name); });
    return Array.from(names).sort();
  }, [jobs]);

  const filterJobs = (jobsList: JobWithEmployer[]) => {
    let filtered = jobsList;
    if (searchFilter.trim()) {
      const search = searchFilter.toLowerCase();
      filtered = filtered.filter(job => 
        job.title.toLowerCase().includes(search) ||
        job.company_name?.toLowerCase().includes(search)
      );
    }
    if (clientFilter) {
      filtered = filtered.filter(job => (job as any).client_name === clientFilter);
    }
    return filtered;
  };

  const handleToggleSelectAll = () => {
    const filteredJobs = filterJobs(jobs);
    if (selectedJobs.length === filteredJobs.length && filteredJobs.length > 0) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(filteredJobs.map(j => j.id!));
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedJobs(prev => 
      prev.includes(id) ? prev.filter(jid => jid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedJobs.length === 0) return;
    
    const confirmMessage = `Ar tikrai norite ištrinti ${selectedJobs.length} skelbimų?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      await Promise.all(selectedJobs.map(id => jobService.deleteJob(id)));
      setSelectedJobs([]);
      loadJobs();
    } catch (error) {
      showToast('Įvyko klaida trinant skelbimus', 'error');
    }
  };

  const handleBulkAction = async () => {
    if (!selectedAction || selectedJobs.length === 0) return;
    
    try {
      if (selectedAction === 'publish') {
        await Promise.all(selectedJobs.map(id => jobService.publishJob(id)));
      } else if (selectedAction === 'delete') {
        await handleDeleteSelected();
        return;
      }
      setSelectedJobs([]);
      setSelectedAction('');
      loadJobs();
    } catch (error) {
      showToast('Įvyko klaida atliekant veiksmą', 'error');
    }
  };

  const filteredJobs = filterJobs(jobs);

  return (
    <div className="job-management">
      <div className="container">
        <div className="header-row">
          <div className="breadcrumbs">
            <Link to="/">Titulinis</Link>
            <ChevronRight size={14} />
            <span>Skelbimai</span>
          </div>
          <div className="filters-toolbar">
            <div className="select-all-dropdown">
              <button 
                className="select-all-btn"
                onClick={handleToggleSelectAll}
              >
                <input 
                  type="checkbox" 
                  checked={selectedJobs.length > 0 && selectedJobs.length === filteredJobs.length}
                  onChange={handleToggleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                />
                <span>Žymėti visus skelbimus</span>
              </button>
            </div>
            <div className="action-dropdown">
              <select 
                value={selectedAction}
                onChange={(e) => setSelectedAction(e.target.value)}
                className="action-select"
              >
                <option value="">Pasirinkti veiksmą</option>
                <option value="publish">Publikuoti</option>
                <option value="delete">Ištrinti</option>
              </select>
              {selectedAction && selectedJobs.length > 0 && (
                <button className="apply-action-btn" onClick={handleBulkAction}>
                  Vykdyti
                </button>
              )}
            </div>
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder="Skelbimo pavadinimas"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
              />
              <Search size={16} className="search-icon" />
            </div>
            {isRecruitmentAgency && uniqueClientNames.length > 0 && (
              <select
                className="client-filter-select"
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
              >
                <option value="">Visi klientai</option>
                {uniqueClientNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            )}
            <button className="btn-new-job" onClick={handleCreateJob}>
              <Plus size={16} />
              <span>Sukurti skelbimą</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert--error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="loading">
            Kraunami darbo skelbimai...
          </div>
        ) : filteredJobs.length === 0 && jobs.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <h3>Jūs dar neturite darbo skelbimų</h3>
            <p>Sukurkite savo pirmąjį darbo skelbimą ir pradėkite ieškoti talentų!</p>
            <button
              className="btn btn--primary"
              onClick={handleCreateJob}
            >
              <Plus size={20} />
              Sukurti pirmąjį skelbimą
            </button>
          </div>
        ) : (
          <div className="jobs-grid">
          {filteredJobs.map((job) => {
            const backgroundImage = getCategoryBackgroundImage(job);
            const logoUrl = getCompanyLogoUrl(job.company_logo);
            const isPromoted = jobPromotions.has(job.id!);
            
              return (
              <div key={job.id} className={`job-card-wrapper ${isPromoted ? 'has-badge' : ''}`}>
                {/* TOP Badge - outside card for proper positioning */}
                {isPromoted && (
                  <div className="top-badge">TOP</div>
                )}
                <div className={`job-card ${selectedJobs.includes(job.id!) ? 'selected' : ''} ${isPromoted ? 'promoted' : ''}`}>
                  {/* Checkbox */}
                  <div className="job-card__checkbox">
                    <input
                      type="checkbox"
                      checked={selectedJobs.includes(job.id!)}
                      onChange={() => handleToggleSelect(job.id!)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                
                {/* Background Image with Logo - Clickable to edit */}
                <div 
                  className="job-card__header"
                  style={{ backgroundImage: `url(${backgroundImage})` }}
                  onClick={() => handleEditJob(job.id!)}
                >
                  <div className="job-card__logo-container">
                    {logoUrl ? (
                      <img src={logoUrl} alt={job.company_name || 'Company logo'} className="job-card__logo" />
                    ) : (
                      <div className="job-card__logo-placeholder">
                        <Briefcase size={20} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Job Title - Clickable to edit */}
                <div className="job-card__body" onClick={() => handleEditJob(job.id!)}>
                  <h3 className="job-title">{job.title}</h3>
                  <span className="edit-hint"><Pencil size={12} /> Redaguoti</span>
                </div>
                {(job as any).client_name && (
                  <div className="job-card__client-tag">
                    <span className="client-tag">{(job as any).client_name}</span>
                  </div>
                )}

                {/* Publication Period - like cv-card-posting */}
                <div className="job-card__publication">
                  <div className="publication-info">
                    <span className="publication-label">Publikuojamas:</span>
                    <span className="publication-date">
                      nuo {formatDate(job.created_at!)} {job.application_deadline && `iki ${formatDate(job.application_deadline)}`}
                    </span>
                  </div>
                  {job.status === 'pending' ? (
                    <span className="pending-badge">LAUKIA PATVIRTINIMO</span>
                  ) : (
                    <button
                      className="btn-toggle"
                      onClick={() => handlePublishJob(job.id!)}
                    >
                      {job.status === 'published' ? 'IŠJUNGTI PUBLIKAVIMĄ' : 'ĮJUNGTI PUBLIKAVIMĄ'}
                    </button>
                  )}
                </div>

                {/* Promotion Status Row - Darbo skelbimo išskyrimas */}
                <div className="job-card__promotion-row">
                  <div className="promotion-info">
                    <span className="promotion-label">{jobPromotions.has(job.id!) ? 'Iškeltas iki:' : 'Neiškeltas'}</span>
                    {jobPromotions.has(job.id!) && (
                      <span className="promotion-date">{formatDate(jobPromotions.get(job.id!)!.expiresAt)}</span>
                    )}
                  </div>
                  <button
                    className={`btn-promote ${jobPromotions.has(job.id!) ? 'active' : ''}`}
                    onClick={() => navigate('/employer/services?mode=employer')}
                  >
                    {jobPromotions.has(job.id!) ? 'PRATĘSTI' : 'IŠKELTI'}
                  </button>
                </div>

                {/* Location and Salary */}
                <div className="job-card__location-salary">
                  <div className="location">
                    <MapPin size={14} />
                    {job.location}
                  </div>
                  <div className="salary">
                    <span className="salary-label">Atlyginimas</span>
                    <span className="salary-amount">{jobService.formatSalary(job.salary_min, job.salary_max, job.salary_currency)}</span>
                  </div>
                </div>

                {/* Main Action Button - EXACT COPY of cv-card-preview */}
                <button
                  className="job-card__action-button"
                  onClick={() => handleViewApplications(job.id!)}
                >
                  Kandidatai
                  <ArrowRight size={20} />
                </button>
              </div>
              </div>
            );
          })}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobManagement;
